/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect } from 'react';
import { Asset } from '@wit/vtv-x-foundation-lib';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PrivateRoutePath } from 'routes/paths';
import * as Gdpr from 'core/store/ducks/gdpr';

import * as OnDemand from 'store/ducks/onDemand';
import translate from 'services/i18n/translate';

import { Rail as RailComponent, SimpleHeader, Spinner } from 'ui/components';
import Overlay from 'ui/components/Hero/Overlay';
import BackgroundOverlay from 'assets/images/background/LandingPage_BackgroundImage.png';
import RouterUtils from 'services/utils/RouterUtils';
import { StoreState } from 'store/store';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import { RailType } from 'types/Rail';
import { useRouter } from '../../../hooks/useRouter';

interface UriParams {
  menuItemId: string;
}

export default function NextLevel(): JSX.Element | null {
  const uriParams = useParams<UriParams>();
  const history = useHistoryWithState();
  const router = useRouter();
  const dispatch = useDispatch();

  let menuItemId: number | undefined = Number.parseInt(uriParams.menuItemId || '', 10);
  menuItemId = !Number.isNaN(menuItemId) ? menuItemId : undefined;

  const isProcessed = useSelector(OnDemand.Selectors.isProcessed);
  const rails = useSelector(OnDemand.Selectors.getCategoryRails);
  const {
    id = menuItemId,
    channelId,
    title,
    withCategories,
    type,
  } = useSelector((store: StoreState) => OnDemand.Selectors.getRail(store, menuItemId)) || {};
  const isGdprConsent = useSelector(Gdpr.Selectors.hasAgreedToConsent);

  useEffect(() => {
    dispatch(
      OnDemand.ActionCreators.initNextLevel({
        menuItemId: id,
        channelId,
        withCategories: true,
      })
    );
  }, []);

  useEffect(() => {
    if (!isGdprConsent && type === RailType.RECOMMENDATIONS) {
      history.replace(PrivateRoutePath.ONDEMAND);
    }
  }, [history, isGdprConsent, type]);

  if (!isProcessed) {
    return (
      <div className="layout-spinner-container">
        <Spinner isActive />
      </div>
    );
  }

  if (!id || !withCategories) {
    history.replace(PrivateRoutePath.ONDEMAND);
    return null;
  }

  const onCardClick = (asset: Asset, menuItemId_?: number) => {
    router.push({ asset, menuItemId: menuItemId_ });
  };

  const onHeaderClick = (itemId: number | string) => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.ONDEMAND_GRID, [id, itemId]));
  };

  return (
    <div id="OnDemand_nextLevel" className="ondemand-nextlevel template-holder back-nav">
      <Overlay backgroundImage={BackgroundOverlay} />
      {!withCategories ? (
        <SimpleHeader
          subtitle={title || ''}
          backLabel={translate['entrypoint.ondemand']}
          backUrl={PrivateRoutePath.ONDEMAND}
          hideBackIcon
        />
      ) : (
        <SimpleHeader subtitle={title || ''} hideBackIcon />
      )}
      {rails.map((rail) => {
        if (!rail.loadingAssets && !rail.assets.length) {
          return null;
        }
        return (
          <div key={rail.channelId}>
            <RailComponent
              railId={rail.id}
              menuItemId={rail.id}
              className="no-background"
              railName={rail.title}
              channelId={rail.channelId}
              assets={rail.assets}
              hasMore={rail.hasMore}
              isLoadingAssets={rail.loadingAssets}
              onCardClick={onCardClick}
              onHeaderClick={onHeaderClick}
            />
          </div>
        );
      })}
    </div>
  );
}
